// frontend/src/components/LockedNodeOverlay.jsx
import React from 'react';
import { Button, Typography, Tooltip } from 'antd';
import { LockOutlined, CrownOutlined } from '@ant-design/icons';
import { NODE_FEATURES, getProMessage } from '../config/nodeConfig';


const { Text } = Typography;

// Upgrade URL with UTM tracking
const UPGRADE_URL = 'https://wpaiworkflowautomation.com/pricing?utm_source=freedownload&utm_medium=lite';

// Main component for completely locked nodes
export const LockedNodeOverlay = ({ nodeType, children, className = '' }) => {
  const isLite = process.env.REACT_APP_VERSION === 'lite';
  
  if (!isLite) {
    return children; // Show normal node in Pro version
  }
  
  const nodeConfig = NODE_FEATURES[nodeType];
  
  if (!nodeConfig || nodeConfig.lite) {
    return children; // Node is allowed in Lite
  }
  
  // Node is completely locked in Lite - show with red border and pro banner
  return (
    <div className={`locked-node-container ${className}`}>
      {/* Pro Feature Banner */}
      <div className="pro-feature-banner">
        <div className="pro-banner-content">
          <CrownOutlined className="pro-banner-icon" />
          <div className="pro-banner-text">
            <Text strong className="pro-banner-title">Pro Feature</Text>
            <Text className="pro-banner-description">{getProMessage(nodeType)}</Text>
          </div>
          <Button 
            type="primary" 
            size="small"
            icon={<CrownOutlined />}
            onClick={() => window.open(UPGRADE_URL, '_blank')}
            className="pro-banner-button"
          >
            Upgrade
          </Button>
        </div>
      </div>
      
      {/* Node with red border - fully visible but non-interactive */}
      <div className="locked-node-content">
        {children}
      </div>
    </div>
  );
};

// Component for partially locked features within nodes (unchanged)
export const LockedFeature = ({ feature, nodeType, children, inline = false }) => {
  const isLite = process.env.REACT_APP_VERSION === 'lite';
  
  if (!isLite) {
    return children; // Show normal feature in Pro
  }
  
  const nodeConfig = NODE_FEATURES[nodeType];
  const restrictions = nodeConfig?.restrictions?.lite;
  
  if (!restrictions || !restrictions.lockedFeatures?.includes(feature)) {
    return children; // Feature is allowed
  }
  
  // Feature is locked - keep existing implementation
  if (inline) {
    return (
      <Tooltip title={`${feature} is a Pro feature. ${restrictions.lockedMessage || 'Upgrade to unlock.'}`}>
        <span className="locked-feature-inline">
          <LockOutlined className="inline-lock-icon" />
          <span className="locked-text">{children}</span>
        </span>
      </Tooltip>
    );
  }
  
  return (
    <div className="locked-feature-block">
      <div className="locked-feature-overlay">
        <LockOutlined />
        <Text type="secondary" className="locked-feature-text">
          {restrictions.lockedMessage || 'This feature requires Pro'}
        </Text>
        <Button 
          type="primary" 
          size="small"
          onClick={() => window.open(UPGRADE_URL, '_blank')}
          style={{ marginTop: '8px' }}
        >
          Upgrade
        </Button>
      </div>
      <div className="locked-feature-content">
        {children}
      </div>
    </div>
  );
};

// Pro-only upgrade message component (unchanged)
export const ProOnlyContent = ({ feature, nodeType, title, description }) => {
  return (
    <div className="pro-only-config" style={{ 
      background: '#f0f8ff', 
      padding: 16, 
      borderRadius: 6,
      border: '1px dashed #1890ff',
      textAlign: 'center'
    }}>
      <CrownOutlined style={{ fontSize: 24, color: '#1890ff', marginBottom: 8 }} />
      <div style={{ marginBottom: 8, fontWeight: 'bold' }}>
        {title || `${feature} Configuration`}
      </div>
      <div style={{ marginBottom: 12, color: '#666' }}>
        {description || `🔒 ${feature} is available in Pro version`}
      </div>
      <Button 
        type="primary" 
        size="small"
        onClick={() => window.open(UPGRADE_URL, '_blank')}
      >
        Upgrade to Pro
      </Button>
    </div>
  );
};

export default LockedNodeOverlay;